import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { McButton, McTooltip } from '@maersk-global/mds-react-wrapper/components-core';
import queryService from '../../services/queryService';
import './FavoriteButton.css';

interface FavoriteButtonProps {
    queryId: string;
    isFavorite: boolean;
    size?: 'sm' | 'md' | 'lg';
    onToggle?: (isFavorite: boolean) => void;
}

export const FavoriteButton: React.FC<FavoriteButtonProps> = ({
    queryId,
    isFavorite,
    size = 'md',
    onToggle,
}) => {
    const queryClient = useQueryClient();
    // Local state for immediate visual feedback (avoids depending on parent re-render)
    const [localIsFavorite, setLocalIsFavorite] = React.useState(isFavorite);

    // Keep local state in sync when the prop changes (e.g., after cache invalidation)
    React.useEffect(() => {
        setLocalIsFavorite(isFavorite);
    }, [isFavorite]);

    const toggleFavoriteMutation = useMutation({
        mutationFn: (newFavoriteState: boolean) =>
            queryService.toggleFavorite(queryId, newFavoriteState),
        onMutate: async (newFavoriteState: boolean) => {
            // Optimistic update — update local visual state immediately
            setLocalIsFavorite(newFavoriteState);
            onToggle?.(newFavoriteState);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['queries'] });
            queryClient.invalidateQueries({ queryKey: ['favorites'] });
        },
        onError: (error: any) => {
            console.error('Failed to toggle favorite:', error);
            // Revert optimistic update
            setLocalIsFavorite(isFavorite);
            onToggle?.(isFavorite);
        },
    });

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row click propagation
        if (toggleFavoriteMutation.isPending) return;
        toggleFavoriteMutation.mutate(!localIsFavorite);
    };

    return (
        <McTooltip>
            <McButton
                slot="trigger"
                variant="plain"
                appearance="neutral"
                icon="star"
                onClick={handleClick}
                disabled={toggleFavoriteMutation.isPending}
                className={`favorite-button ${localIsFavorite ? 'is-favorite' : ''}`}
                aria-label={localIsFavorite ? 'Remove from favorites' : 'Add to favorites'}
                style={localIsFavorite ? { '--mc-button-plain-color': '#FF6400' } as React.CSSProperties : undefined}
            />
            <span slot="content">{localIsFavorite ? 'Remove from favorites' : 'Add to favorites'}</span>
        </McTooltip>
    );
};

export default FavoriteButton;
